import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  Calendar,
  Clock,
  Image,
  Link as LinkIcon,
  Hash,
  Sparkles,
  Send,
  Save,
  CheckCircle,
  Eye,
  Loader2,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  X,
  Video,
  Film,
  Palette,
  AlertTriangle,
  CheckCircle2,
  Info,
  Trash2,
  Play,
  ImageIcon
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import DashboardLayout from '../components/DashboardLayout';
import { AICaptionGenerator, AIHashtagGenerator } from '../components/AIContentTools';
import { VideoEditor } from '../components/VideoEditor';
import { ImageEditor } from '../components/ImageEditor';
import { CollaborationPanel } from '../components/CollaborationPanel';
import { PerformancePrediction } from '../components/PerformancePrediction';
import PlatformSelector from '../components/composer/PlatformSelector';
import HashtagEditor from '../components/composer/HashtagEditor';
import SchedulePanel from '../components/composer/SchedulePanel';
import BrandValidationPanel from '../components/composer/BrandValidationPanel';
import OptimizationTips from '../components/composer/OptimizationTips';
import MediaAttachments from '../components/composer/MediaAttachments';
import PostPreviewPane from '../components/composer/PostPreviewPane';
import { api, useAuth, isUpgradeError } from '../context/AuthContext';
import { toast } from 'sonner';

const PLATFORMS = [
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2', maxChars: 63206 },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F', maxChars: 2200 },
  { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: '#1DA1F2', maxChars: 280 },
  { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: '#0A66C2', maxChars: 3000 },
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: '#FF0000', maxChars: 5000 },
  { id: 'tiktok', name: 'TikTok', icon: null, emoji: '🎵', color: '#000000', maxChars: 2200 },
  { id: 'pinterest', name: 'Pinterest', icon: null, emoji: '📌', color: '#E60023', maxChars: 500 },
  { id: 'threads', name: 'Threads', icon: null, emoji: '🧵', color: '#000000', maxChars: 500 },
  { id: 'bluesky', name: 'Bluesky', icon: null, emoji: '🦋', color: '#0085FF', maxChars: 300 },
];

const PostComposer = () => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const [searchParams] = useSearchParams();
  const initialCalendarId = searchParams.get('calendar');
  const initialDate = searchParams.get('date');
  const { user } = useAuth();

  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [videoEditorOpen, setVideoEditorOpen] = useState(false);
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [connectedPlatforms, setConnectedPlatforms] = useState({});

  const [formData, setFormData] = useState({
    calendar_id: initialCalendarId || '',
    content: '',
    platforms: [],
    media_urls: [],
    scheduled_at: initialDate ? new Date(initialDate) : null,
    hashtags: [],
    link: '',
    utm_parameters: {}
  });

  const [hashtagInput, setHashtagInput] = useState('');
  const [optimizationTips, setOptimizationTips] = useState(null);
  const [showSchedule, setShowSchedule] = useState(!!initialDate);
  const [scheduledTime, setScheduledTime] = useState('09:00');
  
  // Brand Guidelines Validation State
  const [brandGuidelines, setBrandGuidelines] = useState([]);
  const [brandValidation, setBrandValidation] = useState(null);
  const [validatingBrand, setValidatingBrand] = useState(false);
  const [showBrandPanel, setShowBrandPanel] = useState(true);

  const isEditing = !!postId;

  // Check if the selected calendar requires approval
  const calendarRequiresApproval = (() => {
    if (!formData.calendar_id || calendars.length === 0) return false;
    const cal = calendars.find(c => c.calendar_id === formData.calendar_id);
    return cal?.require_approval || false;
  })();

  // Helper function to render media preview
  const renderMediaPreview = (aspectRatio = 'aspect-video', maxItems = 4) => {
    if (formData.media_urls.length === 0) return null;
    
    const mediaItems = formData.media_urls.slice(0, maxItems);
    const isVideo = (url) => url.includes('/video/') || url.includes('.mp4') || url.includes('.mov');
    const isImage = (url) => url.includes('/images/') || url.includes('.png') || url.includes('.jpg') || url.includes('.jpeg') || url.includes('.gif') || url.includes('.webp');
    
    const getVideoId = (url) => {
      const match = url.match(/vid_[a-f0-9]+/);
      return match ? match[0] : null;
    };
    
    const getImageId = (url) => {
      const match = url.match(/img_[a-f0-9]+/);
      return match ? match[0] : null;
    };
    
    const getFullUrl = (url) => {
      if (url.startsWith('http')) return url;
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    };
    
    if (mediaItems.length === 1) {
      const url = mediaItems[0];
      if (isVideo(url)) {
        return (
          <div className={`${aspectRatio} bg-slate-900 relative`}>
            <video 
              src={getFullUrl(url)} 
              className="w-full h-full object-cover"
              controls
              preload="metadata"
            />
            <Badge className="absolute top-2 left-2 bg-purple-600">Video</Badge>
          </div>
        );
      } else {
        return (
          <div className={`${aspectRatio} bg-slate-100`}>
            <img 
              src={getFullUrl(url)} 
              alt="Post media" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        );
      }
    }
    
    // Multiple media - grid layout
    return (
      <div className={`grid ${mediaItems.length === 2 ? 'grid-cols-2' : 'grid-cols-2'} gap-0.5 ${aspectRatio}`}>
        {mediaItems.map((url, idx) => (
          <div key={idx} className="relative bg-slate-100 overflow-hidden">
            {isVideo(url) ? (
              <>
                <video 
                  src={getFullUrl(url)} 
                  className="w-full h-full object-cover"
                  muted
                  preload="metadata"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <Play className="w-8 h-8 text-white" />
                </div>
              </>
            ) : (
              <img 
                src={getFullUrl(url)} 
                alt={`Media ${idx + 1}`} 
                className="w-full h-full object-cover"
              />
            )}
          </div>
        ))}
        {formData.media_urls.length > maxItems && (
          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
            +{formData.media_urls.length - maxItems} more
          </div>
        )}
      </div>
    );
  };

  const fetchInitialData = useCallback(async () => {
    try {
      const calendarsRes = await api.get('/calendars');
      setCalendars(calendarsRes.data);

      if (postId) {
        const postRes = await api.get(`/posts/${postId}`);
        const post = postRes.data;
        setFormData({
          calendar_id: post.calendar_id,
          content: post.content,
          platforms: post.platforms,
          media_urls: post.media_urls || [],
          scheduled_at: post.scheduled_at ? new Date(post.scheduled_at) : null,
          hashtags: post.hashtags || [],
          link: post.link || '',
          utm_parameters: post.utm_parameters || {}
        });
        if (post.scheduled_at) {
          setShowSchedule(true);
          setScheduledTime(format(new Date(post.scheduled_at), 'HH:mm'));
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Fetch brand guidelines
  useEffect(() => {
    const fetchBrandGuidelines = async () => {
      try {
        const res = await api.get('/brand-guidelines/');
        setBrandGuidelines(res.data);
      } catch (error) {
        console.error('Failed to fetch brand guidelines:', error);
      }
    };
    fetchBrandGuidelines();
  }, []);

  // Track connected platforms for the selected calendar
  useEffect(() => {
    if (!formData.calendar_id || calendars.length === 0) {
      setConnectedPlatforms({});
      return;
    }
    const cal = calendars.find(c => c.calendar_id === formData.calendar_id);
    if (cal?.social_accounts) {
      const connected = {};
      Object.entries(cal.social_accounts).forEach(([platform, acc]) => {
        connected[platform] = {
          connected: !!acc,
          demo: acc?.demo_mode || !acc?.access_token,
          name: acc?.username || acc?.name || platform,
        };
      });
      setConnectedPlatforms(connected);
    } else {
      setConnectedPlatforms({});
    }
  }, [formData.calendar_id, calendars]);

  // Debounced brand validation
  const validateContent = useCallback(async (content) => {
    if (!content.trim() || brandGuidelines.length === 0) {
      setBrandValidation(null);
      return;
    }
    
    setValidatingBrand(true);
    try {
      const res = await api.post('/brand-guidelines/validate-content', null, {
        params: { content }
      });
      setBrandValidation(res.data);
    } catch (error) {
      console.error('Brand validation error:', error);
    } finally {
      setValidatingBrand(false);
    }
  }, [brandGuidelines]);

  // Debounce content validation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.content) {
        validateContent(formData.content);
      } else {
        setBrandValidation(null);
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [formData.content, validateContent]);

  const handlePlatformToggle = (platformId) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter(p => p !== platformId)
        : [...prev.platforms, platformId]
    }));
  };

  const handleAddHashtag = () => {
    if (hashtagInput.trim() && !formData.hashtags.includes(hashtagInput.trim())) {
      const tag = hashtagInput.trim().startsWith('#') 
        ? hashtagInput.trim() 
        : `#${hashtagInput.trim()}`;
      setFormData(prev => ({
        ...prev,
        hashtags: [...prev.hashtags, tag]
      }));
      setHashtagInput('');
    }
  };

  const handleRemoveHashtag = (tag) => {
    setFormData(prev => ({
      ...prev,
      hashtags: prev.hashtags.filter(t => t !== tag)
    }));
  };

  const handleOptimize = async () => {
    if (!formData.content || formData.platforms.length === 0) {
      toast.error('Please add content and select platforms first');
      return;
    }

    setOptimizing(true);
    try {
      const response = await api.post('/ai/optimize-post', {
        content: formData.content,
        platforms: formData.platforms
      });
      setOptimizationTips(response.data);
      toast.success('Optimization tips generated');
    } catch (error) {
      if (!isUpgradeError(error)) toast.error('Failed to get optimization tips');
    } finally {
      setOptimizing(false);
    }
  };

  const handleSave = async (status = 'draft') => {
    if (!formData.calendar_id) {
      toast.error('Please select a calendar');
      return;
    }
    if (!formData.content) {
      toast.error('Please add content');
      return;
    }
    if (formData.platforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    setSaving(true);

    let scheduled_at = null;
    if (showSchedule && formData.scheduled_at) {
      const date = new Date(formData.scheduled_at);
      const [hours, minutes] = scheduledTime.split(':');
      date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      scheduled_at = date.toISOString();
    }

    const postData = {
      ...formData,
      scheduled_at,
      status
    };

    try {
      if (isEditing) {
        await api.put(`/posts/${postId}`, postData);
        toast.success('Post updated');
      } else {
        await api.post('/posts', postData);
        toast.success('Post created');
      }
      navigate('/posts');
    } catch (error) {
      if (!isUpgradeError(error)) toast.error('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishNow = async () => {
    if (!formData.calendar_id) {
      toast.error('Please select a calendar');
      return;
    }
    if (!formData.content) {
      toast.error('Please add content');
      return;
    }
    if (formData.platforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    // If calendar requires approval, submit for approval instead
    if (calendarRequiresApproval) {
      return handleSubmitForApproval();
    }

    setPublishing(true);

    try {
      // Create the post first
      const postData = {
        ...formData,
        scheduled_at: null,
        status: 'draft'
      };

      let createdPostId = postId;

      if (isEditing) {
        await api.put(`/posts/${postId}`, postData);
      } else {
        const createRes = await api.post('/posts', postData);
        createdPostId = createRes.data.post_id;
      }

      // Now publish it
      const publishRes = await api.post(`/posts/${createdPostId}/publish`);
      const platformData = publishRes.data.platform_data || {};

      const successPlatforms = Object.entries(platformData)
        .filter(([_, d]) => d.published)
        .map(([p, d]) => `${p}${d.method === 'api' ? ' (live)' : ' (demo)'}`);
      const failedPlatforms = Object.entries(platformData)
        .filter(([_, d]) => !d.published)
        .map(([p]) => p);

      if (successPlatforms.length > 0) {
        toast.success(`Published to: ${successPlatforms.join(', ')}`);
      }
      if (failedPlatforms.length > 0) {
        toast.error(`Failed on: ${failedPlatforms.join(', ')}`);
      }

      navigate('/posts');
    } catch (error) {
      if (!isUpgradeError(error)) toast.error(error.response?.data?.detail || 'Failed to publish post');
    } finally {
      setPublishing(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!formData.calendar_id || !formData.content || formData.platforms.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      let scheduled_at = null;
      if (showSchedule && formData.scheduled_at) {
        const date = new Date(formData.scheduled_at);
        const [hours, minutes] = scheduledTime.split(':');
        date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        scheduled_at = date.toISOString();
      }

      const postData = {
        ...formData,
        scheduled_at,
        status: 'pending_approval'
      };

      if (isEditing) {
        await api.put(`/posts/${postId}`, postData);
      } else {
        const createRes = await api.post('/posts', postData);
        // Also submit for approval via the workflow endpoint
        await api.post(`/posts/${createRes.data.post_id}/submit-approval`).catch(() => {});
      }

      toast.success('Post submitted for approval' + (scheduled_at ? '. It will be published at the scheduled time once approved.' : ''));
      navigate('/posts');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit for approval');
    } finally {
      setSaving(false);
    }
  };

  const getCharacterCount = () => {
    const count = formData.content.length;
    const selectedPlatforms = PLATFORMS.filter(p => formData.platforms.includes(p.id));
    if (selectedPlatforms.length === 0) return { count, status: 'ok' };

    const minLimit = Math.min(...selectedPlatforms.map(p => p.maxChars));
    if (count > minLimit) return { count, status: 'error', limit: minLimit };
    if (count > minLimit * 0.9) return { count, status: 'warning', limit: minLimit };
    return { count, status: 'ok', limit: minLimit };
  };

  const charInfo = getCharacterCount();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex h-full" data-testid="post-composer-page">
        <div className="flex-1 p-6 lg:p-8 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold text-slate-900">
              {isEditing ? 'Edit Post' : 'Create Post'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleOptimize}
              disabled={optimizing}
              data-testid="optimize-btn"
            >
              {optimizing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2 text-amber-500" />
              )}
              AI Optimize
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSave('draft')}
              disabled={saving || publishing}
              data-testid="save-draft-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Draft
            </Button>
            {showSchedule ? (
              <Button
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={calendarRequiresApproval ? handleSubmitForApproval : () => handleSave('scheduled')}
                disabled={saving || publishing}
                data-testid="schedule-post-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Clock className="h-4 w-4 mr-2" />
                )}
                {calendarRequiresApproval ? 'Submit & Schedule' : 'Schedule'}
              </Button>
            ) : (
              <Button
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={handlePublishNow}
                disabled={saving || publishing}
                data-testid="publish-now-btn"
              >
                {publishing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {calendarRequiresApproval
                  ? (publishing ? 'Submitting...' : 'Submit for Approval')
                  : (publishing ? 'Publishing...' : 'Publish Now')}
              </Button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Calendar Selection */}
            <Card className="border-slate-100">
              <CardContent className="p-4">
                <Label className="mb-2 block">Calendar</Label>
                <Select
                  value={formData.calendar_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, calendar_id: value }))}
                >
                  <SelectTrigger data-testid="calendar-select">
                    <SelectValue placeholder="Select a calendar" />
                  </SelectTrigger>
                  <SelectContent>
                    {calendars.map((cal) => (
                      <SelectItem key={cal.calendar_id} value={cal.calendar_id}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: cal.brand_color }}
                          />
                          {cal.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {/* Platforms */}
            <PlatformSelector
              platforms={PLATFORMS}
              formData={formData}
              connectedPlatforms={connectedPlatforms}
              calendarRequiresApproval={calendarRequiresApproval}
              onToggle={handlePlatformToggle}
            />

            {/* Content */}
            <Card className="border-slate-100">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold">Content</CardTitle>
                  <span className={`text-sm ${
                    charInfo.status === 'error' ? 'text-red-500' :
                    charInfo.status === 'warning' ? 'text-amber-500' :
                    'text-slate-500'
                  }`}>
                    {charInfo.count}{charInfo.limit && ` / ${charInfo.limit}`}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {/* AI Tools Toolbar */}
                <div className="flex items-center gap-2 flex-wrap p-2 bg-gradient-to-r from-slate-50 to-purple-50/30 rounded-lg border border-slate-100">
                  <span className="text-xs text-slate-500 font-medium mr-1">AI Tools:</span>
                  <AICaptionGenerator
                    currentContent={formData.content}
                    platform={formData.platforms[0] || 'general'}
                    onApplyCaption={(caption) => setFormData(prev => ({ ...prev, content: caption }))}
                    onApplyHashtags={(tags) => {
                      const normalizedTags = tags.map(t => t.startsWith('#') ? t : `#${t}`);
                      setFormData(prev => ({
                        ...prev,
                        hashtags: [...new Set([...prev.hashtags, ...normalizedTags])]
                      }));
                    }}
                  />
                  <AIHashtagGenerator
                    content={formData.content}
                    platform={formData.platforms[0] || 'general'}
                    onApplyHashtags={(tags) => {
                      const normalizedTags = tags.map(t => t.startsWith('#') ? t : `#${t}`);
                      setFormData(prev => ({
                        ...prev,
                        hashtags: [...new Set([...prev.hashtags, ...normalizedTags])]
                      }));
                    }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                    onClick={() => setVideoEditorOpen(true)}
                    data-testid="ai-video-btn"
                  >
                    <Film className="h-4 w-4" />
                    Video Studio
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 border-blue-200 text-blue-600 hover:bg-blue-50"
                    onClick={() => setImageEditorOpen(true)}
                    data-testid="ai-image-btn"
                  >
                    <ImageIcon className="h-4 w-4" />
                    Image Studio
                  </Button>
                </div>
                
                <Textarea
                  placeholder="What's on your mind?"
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="min-h-[200px] resize-none border-slate-200 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  data-testid="post-content-input"
                />
              </CardContent>
            </Card>

            {/* Media Attachments */}
            <MediaAttachments
              mediaUrls={formData.media_urls}
              onRemove={(index) => setFormData(prev => ({ ...prev, media_urls: prev.media_urls.filter((_, i) => i !== index) }))}
              onClearAll={() => setFormData(prev => ({ ...prev, media_urls: [] }))}
              onAddVideo={() => setVideoEditorOpen(true)}
            />

            {/* Hashtags */}
            <HashtagEditor
              hashtags={formData.hashtags}
              hashtagInput={hashtagInput}
              onInputChange={setHashtagInput}
              onAdd={handleAddHashtag}
              onRemove={handleRemoveHashtag}
            />

            {/* Link */}
            <Card className="border-slate-100">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <LinkIcon className="h-4 w-4" />
                  Link
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Input
                  placeholder="https://..."
                  value={formData.link}
                  onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                  data-testid="link-input"
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Schedule */}
            <SchedulePanel
              showSchedule={showSchedule}
              onToggle={setShowSchedule}
              scheduledAt={formData.scheduled_at}
              onDateChange={(date) => setFormData(prev => ({ ...prev, scheduled_at: date }))}
              scheduledTime={scheduledTime}
              onTimeChange={setScheduledTime}
            />

            {/* Brand Guidelines Validation */}
            <BrandValidationPanel
              brandGuidelines={brandGuidelines}
              brandValidation={brandValidation}
              validatingBrand={validatingBrand}
              content={formData.content}
            />

            {/* Performance Prediction */}
            <PerformancePrediction
              content={formData.content}
              platforms={formData.platforms}
              hashtags={formData.hashtags}
              scheduledHour={showSchedule && scheduledTime ? parseInt(scheduledTime.split(':')[0]) : null}
              mediaUrls={formData.media_urls}
            />

            {/* Preview */}
            <PostPreviewPane
              platforms={PLATFORMS}
              platformData={PLATFORMS}
              formData={formData}
              renderMediaPreview={renderMediaPreview}
            />

            {/* Optimization Tips */}
            <OptimizationTips
              tips={optimizationTips}
              onApplyContent={(content) => setFormData(prev => ({ ...prev, content }))}
              onAddHashtag={(tag) => {
                const t = tag.startsWith('#') ? tag : `#${tag}`;
                if (!formData.hashtags.includes(t)) {
                  setFormData(prev => ({ ...prev, hashtags: [...prev.hashtags, t] }));
                }
              }}
            />
          </div>
        </div>
      </div>
      
      {/* Video Editor Dialog */}
      <VideoEditor
        isOpen={videoEditorOpen}
        onClose={() => setVideoEditorOpen(false)}
        onVideoSelect={(url) => {
          setFormData(prev => ({
            ...prev,
            media_urls: [...prev.media_urls, url]
          }));
          toast.success('Video added to post!');
        }}
      />
      
      {/* Image Editor Dialog */}
      <ImageEditor
        open={imageEditorOpen}
        onClose={() => setImageEditorOpen(false)}
        onImageSelect={(url) => {
          setFormData(prev => ({
            ...prev,
            media_urls: [...prev.media_urls, url]
          }));
          toast.success('Image added to post!');
        }}
      />
        {/* End of flex-1 content area (closed at line 1224) */}

        {/* Collaboration Panel */}
        {postId && user && (
          <CollaborationPanel
            postId={postId}
            userId={user.user_id}
            userName={user.name || user.email}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default PostComposer;
